import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { CalendarOff, ChevronLeft, ChevronRight, Settings2, Users } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { useIsMobile, useReducedMotion } from '../../shared/hooks/useMediaQuery';
import { useSelectedBatch } from '../../shared/hooks/useSelectedBatch';
import { readAttendanceView, writeAttendanceView } from '../../shared/lib/selected-batch-store';
import { Button } from '../../shared/ui/Button';
import { DesktopToolNote } from '../../shared/ui/DesktopToolNote';
import { Skeleton } from '../../shared/ui/Skeleton';
import { StatusBanner } from '../../shared/ui/StatusBanner';

import {
  BATCH_ATTENDANCE_QUERY_KEYS,
  type BatchRow,
  getBatchDailyAttendance,
  getBatchWorkdayConfig,
  getInstituteAttendanceSummary,
  INSTITUTE_QUERY_KEYS,
  listBatchHolidays,
  listInstituteBatches,
} from './api';
import { isAtPresent, type RangeView, rangeFor, stepAnchor } from './attendance-range';
import s from './AttendanceBoard.module.css';
import { AttendanceMatrix } from './AttendanceMatrix';
import { BatchRail } from './BatchRail';
import { DailyAttendancePanel } from './DailyAttendancePanel';
import { type RangeBatch, useAttendanceRange } from './useAttendanceRange';

const PANEL_ID = 'attendance-board-roster';
/** Sentinel batch id for the "every batch at once" scope. */
const ALL_BATCHES = '__all_batches__';
/** How far back "go to the last class day" is willing to look. */
const LOOKBACK_DAYS = 21;
/** Window used to pull holidays for the look-back walk. */
const HOLIDAY_WINDOW_DAYS = 40;

const VIEW_OPTIONS: { id: RangeView; label: string }[] = [
  { id: 'day', label: 'Day' },
  { id: 'week', label: 'Week' },
  { id: 'month', label: 'Month' },
];

const NO_BATCHES: readonly RangeBatch[] = [];
const ALL_EXTRA_IDS: readonly string[] = [ALL_BATCHES];
const NO_EXTRA_IDS: readonly string[] = [];

/**
 * The week is the useful default: it shows today ready to mark AND the days
 * just gone, which is the thing a teacher actually opens the dashboard to
 * check. A remembered choice wins over it.
 */
const DEFAULT_VIEW: RangeView = 'week';

function isRangeView(value: string | null): value is RangeView {
  return value === 'day' || value === 'week' || value === 'month';
}

function isWorkdayFor(mask: number, date: dayjs.Dayjs, holidays: ReadonlySet<string>): boolean {
  // Bit N of the mask is day N of the week, 0 = Sunday (matches Postgres
  // `extract(dow from date)` and public.is_batch_workday).
  if ((mask & (1 << date.day())) === 0) return false;
  return !holidays.has(date.format('YYYY-MM-DD'));
}

interface Props {
  instituteId: string;
}

export function AttendanceBoard({ instituteId }: Props): JSX.Element {
  const queryClient = useQueryClient();
  const today = useMemo(() => dayjs().format('YYYY-MM-DD'), []);
  const sectionRef = useRef<HTMLElement | null>(null);
  const isMobile = useIsMobile();
  const reducedMotion = useReducedMotion();
  // Read during the initialiser rather than through an effect, so a reload
  // paints the remembered span on the first frame with no flash of another one.
  const [view, setViewState] = useState<RangeView>(() => {
    const stored = readAttendanceView(instituteId);
    if (isRangeView(stored)) return stored;
    // No remembered preference: a week/month span is cramped on a phone-width
    // screen (this is the grid AttendanceMatrix has to lay out sideways), so
    // a first-ever visit from one starts on the single-day view instead. Once
    // the teacher picks anything, that choice is what's remembered from then on.
    return isMobile ? 'day' : DEFAULT_VIEW;
  });
  const [date, setDate] = useState(today);

  // Switching span or scope can change the card's height by several hundred
  // pixels, which shoves whatever the teacher was looking at off screen. Pull
  // the board back to the top of the viewport so the change stays in view.
  const keepBoardInView = useCallback((): void => {
    const el = sectionRef.current;
    // Not implemented in jsdom, and purely cosmetic — never let it throw.
    if (typeof el?.scrollIntoView !== 'function') return;
    el.scrollIntoView({ block: 'start', behavior: reducedMotion ? 'auto' : 'smooth' });
  }, [reducedMotion]);

  const setView = useCallback(
    (next: RangeView): void => {
      setViewState(next);
      writeAttendanceView(instituteId, next);
      keepBoardInView();
    },
    [instituteId, keepBoardInView],
  );

  const batchesQuery = useQuery({
    queryKey: INSTITUTE_QUERY_KEYS.batches(instituteId),
    queryFn: () => listInstituteBatches(instituteId),
  });
  const batches = useMemo(() => batchesQuery.data ?? [], [batchesQuery.data]);
  const batchIds = useMemo(() => batches.map((b) => b.id), [batches]);
  // One batch is already "all of them" — the extra scope would just be a second
  // way to select the same rows.
  const canSelectAll = batches.length > 1;
  const { selectedBatchId, selectBatch } = useSelectedBatch(
    instituteId,
    batchIds,
    canSelectAll ? ALL_EXTRA_IDS : NO_EXTRA_IDS,
  );
  const isAll = selectedBatchId === ALL_BATCHES;

  // Same reasoning as the span switch: "All batches" can be ten times the rows
  // of a single batch.
  const handleSelectBatch = useCallback(
    (batchId: string): void => {
      selectBatch(batchId);
      if (batchId === ALL_BATCHES || selectedBatchId === ALL_BATCHES) keepBoardInView();
    },
    [selectBatch, selectedBatchId, keepBoardInView],
  );

  const range = useMemo(() => rangeFor(view, date), [view, date]);
  // The single-batch day view keeps the roster panel: it can take notes and
  // mark everyone present at once, neither of which a one-column grid can do.
  const usesMatrix = isAll || view !== 'day';

  // Counts for every batch on this date — drives the rail badges. Soft-degrades
  // to `null` when the migration behind it hasn't been applied.
  const summaryQuery = useQuery({
    queryKey: INSTITUTE_QUERY_KEYS.attendanceSummary(instituteId, date),
    queryFn: () => getInstituteAttendanceSummary(instituteId, date),
  });

  const dayQuery = useQuery({
    queryKey:
      selectedBatchId !== null && !isAll
        ? BATCH_ATTENDANCE_QUERY_KEYS.day(selectedBatchId, date)
        : ['institute', 'batch_daily_attendance', 'disabled'],
    queryFn: () => getBatchDailyAttendance(selectedBatchId as string, date),
    enabled: selectedBatchId !== null && !usesMatrix,
    // Switching batch or stepping the date keeps the previous roster on screen
    // instead of collapsing the card to a spinner and shifting the page.
    placeholderData: keepPreviousData,
  });

  // Only the scope actually on screen is fetched — an empty list means the
  // range hook issues no queries at all.
  const rangeBatches = useMemo<readonly RangeBatch[]>(() => {
    if (!usesMatrix) return NO_BATCHES;
    if (isAll) return batches.map((b) => ({ id: b.id, name: b.name }));
    const one = batches.find((b) => b.id === selectedBatchId);
    return one === undefined ? NO_BATCHES : [{ id: one.id, name: one.name }];
  }, [usesMatrix, isAll, batches, selectedBatchId]);
  const matrix = useAttendanceRange(rangeBatches, range.from, range.to);

  const holidayWindow = useMemo(() => {
    const to = dayjs(date);
    return {
      from: to.subtract(HOLIDAY_WINDOW_DAYS, 'day').format('YYYY-MM-DD'),
      to: to.format('YYYY-MM-DD'),
    };
  }, [date]);

  const day = dayQuery.data;
  const isOffDay = day !== undefined && !day.is_workday;

  // These two answer "which earlier day was a class day?". They run alongside
  // the roster rather than waiting for `isOffDay`: with a default Mon–Fri mask
  // every weekend lands on the off-day card, and gating them behind it made the
  // "Go to <last class day>" button pop in a beat after the card. Both are
  // small and cached per batch.
  const configQuery = useQuery({
    queryKey:
      selectedBatchId !== null && !isAll
        ? BATCH_ATTENDANCE_QUERY_KEYS.workdayConfig(selectedBatchId)
        : ['institute', 'batch_workday_config', 'disabled'],
    queryFn: () => getBatchWorkdayConfig(selectedBatchId as string),
    enabled: selectedBatchId !== null && !usesMatrix,
  });
  const holidaysQuery = useQuery({
    queryKey:
      selectedBatchId !== null && !isAll
        ? BATCH_ATTENDANCE_QUERY_KEYS.holidays(
            selectedBatchId,
            holidayWindow.from,
            holidayWindow.to,
          )
        : ['institute', 'batch_holidays', 'disabled'],
    queryFn: () =>
      listBatchHolidays(selectedBatchId as string, holidayWindow.from, holidayWindow.to),
    enabled: selectedBatchId !== null && !usesMatrix,
  });

  const lastClassDay = useMemo(() => {
    if (!isOffDay || configQuery.data === undefined || holidaysQuery.data === undefined) {
      return null;
    }
    const mask = configQuery.data.workday_mask;
    const holidays = new Set(holidaysQuery.data.map((h) => h.holiday_date));
    for (let i = 1; i <= LOOKBACK_DAYS; i += 1) {
      const candidate = dayjs(date).subtract(i, 'day');
      if (isWorkdayFor(mask, candidate, holidays)) return candidate.format('YYYY-MM-DD');
    }
    return null;
  }, [isOffDay, configQuery.data, holidaysQuery.data, date]);

  const prefetchBatch = useCallback(
    (batchId: string): void => {
      void queryClient.prefetchQuery({
        queryKey: BATCH_ATTENDANCE_QUERY_KEYS.day(batchId, date),
        queryFn: () => getBatchDailyAttendance(batchId, date),
      });
      // The off-day path needs both of these to offer "go to last class day";
      // warming them here keeps that first switch from stalling.
      void queryClient.prefetchQuery({
        queryKey: BATCH_ATTENDANCE_QUERY_KEYS.workdayConfig(batchId),
        queryFn: () => getBatchWorkdayConfig(batchId),
      });
      void queryClient.prefetchQuery({
        queryKey: BATCH_ATTENDANCE_QUERY_KEYS.holidays(
          batchId,
          holidayWindow.from,
          holidayWindow.to,
        ),
        queryFn: () => listBatchHolidays(batchId, holidayWindow.from, holidayWindow.to),
      });
    },
    [queryClient, date, holidayWindow.from, holidayWindow.to],
  );

  const atPresent = isAtPresent(view, date, today);

  const stepRange = useCallback(
    (delta: number): void => {
      setDate((current) => stepAnchor(view, current, delta, today));
    },
    [view, today],
  );

  const selectedBatch = batches.find((b) => b.id === selectedBatchId) ?? null;
  const dateLabel = dayjs(date).format('ddd, D MMM YYYY');
  const isToday = date === today;

  return (
    <section className={s.root} ref={sectionRef} aria-labelledby="attendance-board-title">
      <header className={s.head}>
        <div className={s.headText}>
          <span className={s.eyebrow}>Daily attendance</span>
          <h2 className={s.title} id="attendance-board-title">
            {view === 'day' && isToday ? 'Today’s attendance' : 'Attendance'}
          </h2>
        </div>
        {selectedBatch !== null ? (
          <div className={s.headActions}>
            <Link
              to={`/institute/batches/${selectedBatch.id}/schedule`}
              className={s.headLink}
              aria-label={`Edit the class schedule for ${selectedBatch.name}`}
            >
              <Settings2 size={14} aria-hidden="true" />
              Schedule
            </Link>
            <Link
              to={`/institute/batches/${selectedBatch.id}/attendance`}
              className={s.headLink}
              aria-label={`Open the full attendance register for ${selectedBatch.name}`}
            >
              Full register →
            </Link>
          </div>
        ) : null}
      </header>

      <BoardBody
        batchesPending={batchesQuery.isPending}
        batchesError={batchesQuery.error}
        batches={batches}
        summaries={summaryQuery.data}
        selectedBatchId={selectedBatchId}
        canSelectAll={canSelectAll}
        onSelect={handleSelectBatch}
        onPrefetch={prefetchBatch}
        view={view}
        onViewChange={setView}
        date={date}
        dateLabel={dateLabel}
        rangeLabel={range.label}
        atPresent={atPresent}
        today={today}
        onStepRange={stepRange}
        onPickDate={setDate}
        usesMatrix={usesMatrix}
        matrix={matrix}
        matrixDates={range.dates}
        isAll={isAll}
        dayQuery={dayQuery}
        isToday={isToday}
        lastClassDay={lastClassDay}
        selectedBatchName={selectedBatch?.name ?? ''}
      />
    </section>
  );
}

interface BoardBodyProps {
  batchesPending: boolean;
  batchesError: Error | null;
  batches: BatchRow[];
  summaries: Awaited<ReturnType<typeof getInstituteAttendanceSummary>> | undefined;
  selectedBatchId: string | null;
  canSelectAll: boolean;
  onSelect: (batchId: string) => void;
  onPrefetch: (batchId: string) => void;
  view: RangeView;
  onViewChange: (view: RangeView) => void;
  date: string;
  dateLabel: string;
  rangeLabel: string;
  atPresent: boolean;
  today: string;
  onStepRange: (delta: number) => void;
  onPickDate: (date: string) => void;
  usesMatrix: boolean;
  matrix: ReturnType<typeof useAttendanceRange>;
  matrixDates: string[];
  isAll: boolean;
  dayQuery: ReturnType<typeof useQuery<Awaited<ReturnType<typeof getBatchDailyAttendance>>, Error>>;
  isToday: boolean;
  lastClassDay: string | null;
  selectedBatchName: string;
}

function BoardBody({
  batchesPending,
  batchesError,
  batches,
  summaries,
  selectedBatchId,
  canSelectAll,
  onSelect,
  onPrefetch,
  view,
  onViewChange,
  date,
  dateLabel,
  rangeLabel,
  atPresent,
  today,
  onStepRange,
  onPickDate,
  usesMatrix,
  matrix,
  matrixDates,
  isAll,
  dayQuery,
  isToday,
  lastClassDay,
  selectedBatchName,
}: BoardBodyProps): JSX.Element {
  if (batchesError !== null) {
    return <StatusBanner tone="error">Could not load batches: {batchesError.message}</StatusBanner>;
  }
  if (batchesPending) {
    return <LoadingRows label="Loading batches" />;
  }
  if (batches.length === 0) {
    return (
      <div className={s.empty}>
        <span className={s.emptyIcon} aria-hidden="true">
          <Users size={22} />
        </span>
        <h3 className={s.emptyTitle}>No batches yet</h3>
        <p className={s.emptyBody}>
          Attendance is taken per batch. Create a batch, add students to it, and the register shows
          up here.
        </p>
        <Link to="/institute/batches" className={s.emptyCta}>
          Create a batch →
        </Link>
      </div>
    );
  }

  const busy = usesMatrix ? matrix.isFetching : dayQuery.isFetching;

  return (
    <>
      {selectedBatchId !== null ? (
        <BatchRail
          batches={batches}
          summaries={summaries}
          selectedBatchId={selectedBatchId}
          onSelect={onSelect}
          onPrefetch={onPrefetch}
          panelId={PANEL_ID}
          {...(canSelectAll ? { allId: ALL_BATCHES } : {})}
        />
      ) : null}

      <div className={s.controls}>
        <div className={s.viewSwitch} role="group" aria-label="How much of the register to show">
          {VIEW_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              className={s.viewBtn}
              aria-pressed={view === option.id}
              onClick={() => onViewChange(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className={s.dateBar}>
          <button
            type="button"
            className={s.iconBtn}
            aria-label={`Previous ${view}`}
            onClick={() => onStepRange(-1)}
          >
            <ChevronLeft size={16} aria-hidden="true" />
          </button>
          <label className={s.dateField}>
            <span className={s.srOnly}>Attendance date</span>
            <input
              type="date"
              className={s.dateInput}
              value={date}
              max={today}
              onChange={(e) => onPickDate(e.target.value)}
            />
          </label>
          <button
            type="button"
            className={s.iconBtn}
            aria-label={`Next ${view}`}
            disabled={atPresent}
            onClick={() => onStepRange(1)}
          >
            <ChevronRight size={16} aria-hidden="true" />
          </button>
          <span className={s.dateLabel} aria-live="polite">
            {view === 'day' ? dateLabel : rangeLabel}
          </span>
          {!atPresent ? (
            <Button variant="ghost" onClick={() => onPickDate(today)}>
              Today
            </Button>
          ) : null}
        </div>
      </div>

      <div
        id={PANEL_ID}
        role="tabpanel"
        aria-labelledby={selectedBatchId !== null ? `batch-tab-${selectedBatchId}` : undefined}
        // While a switch is in flight the previous roster stays visible and is
        // dimmed, rather than the card collapsing to a spinner.
        className={busy ? s.panelBusy : undefined}
        aria-busy={busy || undefined}
      >
        {usesMatrix ? (
          <MatrixView
            matrix={matrix}
            dates={matrixDates}
            rangeLabel={rangeLabel}
            isAll={isAll}
            view={view}
          />
        ) : (
          <RosterView
            dayQuery={dayQuery}
            batchId={selectedBatchId}
            date={date}
            dateLabel={dateLabel}
            isToday={isToday}
            lastClassDay={lastClassDay}
            onPickDate={onPickDate}
            batchName={selectedBatchName}
            isStale={dayQuery.isPlaceholderData}
          />
        )}
      </div>
    </>
  );
}

/** Three plates the height of a roster row, in the same box the roster fills. */
const LOADING_ROWS = [0, 1, 2] as const;

function LoadingRows({ label }: { label: string }): JSX.Element {
  return (
    <div className={s.loadingRows} role="status" aria-label={label}>
      {LOADING_ROWS.map((row) => (
        <Skeleton key={row} height={44} radius="md" />
      ))}
    </div>
  );
}

interface MatrixViewProps {
  matrix: ReturnType<typeof useAttendanceRange>;
  dates: string[];
  rangeLabel: string;
  isAll: boolean;
  view: RangeView;
}

function MatrixView({ matrix, dates, rangeLabel, isAll, view }: MatrixViewProps): JSX.Element {
  // One batch failing must not blank the batches that loaded — in "All
  // batches" a single bad month query would otherwise hide the whole board.
  if (matrix.error !== null && matrix.groups.length === 0) {
    return <StatusBanner tone="error">{matrix.error.message}</StatusBanner>;
  }
  if (matrix.isPending && matrix.groups.length === 0) {
    return <LoadingRows label="Loading attendance" />;
  }
  return (
    <>
      {/* A month of columns is honestly a desktop grid; say so once per tab. */}
      {view === 'month' ? <DesktopToolNote id="attendance-month" /> : null}
      {matrix.error !== null ? (
        <StatusBanner tone="warning">
          Some batches could not be loaded: {matrix.error.message}
        </StatusBanner>
      ) : null}
      <AttendanceMatrix
        dates={dates}
        groups={matrix.groups}
        showGroupHeaders={isAll}
        density={view === 'month' ? 'compact' : 'comfortable'}
        rangeLabel={rangeLabel}
        eyebrow={isAll ? 'All batches' : 'Attendance'}
        emptyTitle={isAll ? 'No students in any batch yet' : 'No students in this batch'}
        emptyBody="Assign students from the Batches page and their attendance shows up here."
      />
    </>
  );
}

interface RosterViewProps {
  dayQuery: BoardBodyProps['dayQuery'];
  batchId: string | null;
  date: string;
  dateLabel: string;
  isToday: boolean;
  lastClassDay: string | null;
  onPickDate: (date: string) => void;
  batchName: string;
  /** True while `keepPreviousData` is showing the OUTGOING batch/date's data. */
  isStale: boolean;
}

function RosterView({
  dayQuery,
  batchId,
  date,
  dateLabel,
  isToday,
  lastClassDay,
  onPickDate,
  batchName,
  isStale,
}: RosterViewProps): JSX.Element {
  if (batchId === null) {
    return <p className={s.loading}>Pick a batch to start.</p>;
  }
  if (dayQuery.error !== null) {
    return <StatusBanner tone="error">{dayQuery.error.message}</StatusBanner>;
  }
  const day = dayQuery.data;
  if (day === undefined) {
    return <LoadingRows label="Loading attendance" />;
  }

  // Holding the outgoing roster's ROWS while the next one loads reads as a
  // dimmed list — fine. Holding its off-day/empty state does not: those branches
  // print the newly-selected batch's name next to the previous batch's reason
  // ("Evening B has no class — Marked as a holiday: Founders Day" where the
  // holiday belongs to Morning A). Wait for fresh data before asserting either.
  if (isStale && (!day.is_workday || day.rows.length === 0)) {
    return <LoadingRows label="Loading attendance" />;
  }

  if (!day.is_workday) {
    const reason = day.holiday_reason;
    return (
      <div className={s.offDay}>
        <span className={s.offIcon} aria-hidden="true">
          <CalendarOff size={22} />
        </span>
        <h3 className={s.offTitle}>
          {batchName === '' ? 'No class' : `${batchName} has no class`}{' '}
          {isToday ? 'today' : `on ${dateLabel}`}
        </h3>
        <p className={s.offBody}>
          {reason !== null && reason !== ''
            ? `Marked as a holiday: ${reason}.`
            : `${dayjs(date).format('dddd')}s are outside this batch’s class days.`}{' '}
          Attendance can’t be marked on a non-class day.
        </p>
        <div className={s.offActions}>
          {lastClassDay !== null ? (
            <Button variant="primary" onClick={() => onPickDate(lastClassDay)}>
              Go to {dayjs(lastClassDay).format('ddd, D MMM')}
            </Button>
          ) : null}
          <Link to={`/institute/batches/${batchId}/schedule`} className={s.offLink}>
            Edit schedule →
          </Link>
        </div>
      </div>
    );
  }

  if (day.rows.length === 0) {
    return (
      <div className={s.empty}>
        <span className={s.emptyIcon} aria-hidden="true">
          <Users size={22} />
        </span>
        <h3 className={s.emptyTitle}>No students in this batch</h3>
        <p className={s.emptyBody}>
          Add students to {batchName === '' ? 'this batch' : batchName} and their names appear here
          ready to mark.
        </p>
        <Link to="/institute/batches" className={s.emptyCta}>
          Manage batch students →
        </Link>
      </div>
    );
  }

  return <DailyAttendancePanel batchId={batchId} date={date} day={day} />;
}
